/**
 * @fileoverview Adaptador DRE ↔ HistoricalData (ValuationEngine)
 * @module lib/dre/adapters
 * 
 * Converte dados entre os formatos:
 * - DREData (sistema editável) ↔ HistoricalData[] (ValuationEngine)
 * 
 * Isso permite que dados editados na DRE alimentem automaticamente
 * os cálculos de valuation sem modificar o ValuationEngine existente.
 * 
 * @version 1.1.0 - Interface HistoricalData agora local (independente de v1)
 */

import type { DREData, DREYear } from '../../types/dre';

/**
 * Interface para dados históricos de ambas as empresas
 * Migrada do ValuationEngine v1 para manter compatibilidade com adapters DRE
 */
export interface HistoricalData {
  year: number;
  setima: {
    receita: number;
    ebitda: number;
    margemEbitda: number;
  };
  studio555: {
    receita: number;
    ebitda: number;
    margemEbitda: number;
  };
}

// ============================================================================
// CONVERSÃO: DREData → HistoricalData[]
// ============================================================================

/**
 * Converte DREData (formato editável) para HistoricalData[] (formato ValuationEngine)
 * 
 * @param dreData - Dados DRE editáveis (Sétima + 555 Studios)
 * @returns Array de HistoricalData para uso no ValuationEngine
 * 
 * @example
 * ```typescript
 * const dreData = await loadDRE();
 * const historicalData = dreToHistoricalData(dreData);
 * const engine = new ValuationEngine(historicalData, assumptions, ...);
 * const result = engine.calculate();
 * ```
 */
export function dreToHistoricalData(
  dreData: DREData | Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): HistoricalData[] {
  // Coletar todos os anos únicos de ambas empresas
  const setimaYears = 'setima' in dreData && dreData.setima.years 
    ? dreData.setima.years 
    : [];
  const studio555Years = 'studio555' in dreData && dreData.studio555.years 
    ? dreData.studio555.years 
    : [];

  const allYears = new Set<number>([
    ...setimaYears.map(y => y.year),
    ...studio555Years.map(y => y.year),
  ]);

  // Converter cada ano
  return Array.from(allYears)
    .sort((a, b) => a - b) // Ordenar cronologicamente
    .map(year => {
      const setimaYear = setimaYears.find(y => y.year === year);
      const studio555Year = studio555Years.find(y => y.year === year);

      return {
        year,
        setima: dreYearToCompanyData(setimaYear),
        studio555: dreYearToCompanyData(studio555Year),
      };
    });
}

/**
 * Converte um DREYear para o formato de empresa do HistoricalData
 * Se o ano não existir, retorna valores zerados
 */
function dreYearToCompanyData(
  dreYear: DREYear | undefined
): { receita: number; ebitda: number; margemEbitda: number } {
  if (!dreYear) {
    return { receita: 0, ebitda: 0, margemEbitda: 0 };
  }

  return {
    receita: dreYear.receitaLiquida,
    ebitda: dreYear.ebitda,
    margemEbitda: dreYear.margemEbitda / 100, // Converter % para decimal
  };
}

// ============================================================================
// CONVERSÃO REVERSA: HistoricalData[] → DREData
// ============================================================================

/**
 * Converte HistoricalData[] (formato ValuationEngine) para DREData (formato editável)
 * 
 * NOTA: Esta conversão resulta em dados PARCIAIS, pois HistoricalData contém
 * apenas receita, EBITDA e margem EBITDA. Outros campos da DRE serão zerados.
 * 
 * Use esta função apenas para:
 * - Migração inicial de dados históricos
 * - Testes
 * - Importação de dados legados
 * 
 * @param historicalData - Array de dados históricos do ValuationEngine
 * @returns DREData parcial (apenas receita, EBITDA preenchidos)
 * 
 * @example
 * ```typescript
 * import { DEFAULT_HISTORICAL_DATA } from './core/ValuationEngine';
 * const dreData = historicalDataToDRE(DEFAULT_HISTORICAL_DATA);
 * // Agora pode salvar no banco como versão master
 * ```
 */
export function historicalDataToDRE(
  historicalData: HistoricalData[]
): Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'> {
  // Separar anos por empresa
  const setimaYears: DREYear[] = [];
  const studio555Years: DREYear[] = [];

  historicalData.forEach(yearData => {
    // Processar Sétima
    if (yearData.setima.receita > 0 || yearData.setima.ebitda !== 0) {
      setimaYears.push(companyDataToDREYear(yearData.year, yearData.setima));
    }

    // Processar 555 Studios
    if (yearData.studio555.receita > 0 || yearData.studio555.ebitda !== 0) {
      studio555Years.push(companyDataToDREYear(yearData.year, yearData.studio555));
    }
  });

  return {
    setima: {
      companyId: 'setima',
      companyName: 'Sétima',
      years: setimaYears,
    },
    studio555: {
      companyId: '555',
      companyName: '555 Studios',
      years: studio555Years,
    },
  };
}

/**
 * Converte dados de empresa do HistoricalData para DREYear
 * ATENÇÃO: Campos não disponíveis em HistoricalData serão zerados
 */
function companyDataToDREYear(
  year: number,
  companyData: { receita: number; ebitda: number; margemEbitda: number }
): DREYear {
  return {
    year,
    
    // ========================================
    // Campos EDITÁVEIS (parcialmente preenchidos)
    // ========================================
    receitaBruta: companyData.receita, // Assumir que receita líquida = bruta
    impostosVendas: 0,                 // ❌ Não disponível
    custosDirectos: 0,                 // ❌ Não disponível
    despesasOperacionais: 0,           // ❌ Não disponível
    depreciacao: 0,                    // ❌ Não disponível
    impostosLucro: 0,                  // ❌ Não disponível
    
    // ========================================
    // Campos CALCULADOS
    // ========================================
    receitaLiquida: companyData.receita,
    lucroBruto: 0,                     // ❌ Não disponível
    margemBruta: 0,                    // ❌ Não disponível
    ebitda: companyData.ebitda,
    margemEbitda: companyData.margemEbitda * 100, // Converter decimal para %
    ebit: 0,                           // ❌ Não disponível
    margemEbit: 0,                     // ❌ Não disponível
    lucroLiquido: 0,                   // ❌ Não disponível
    margemLiquida: 0,                  // ❌ Não disponível
  };
}

// ============================================================================
// VALIDAÇÃO DE CONVERSÃO
// ============================================================================

/**
 * Valida se a conversão DRE → Historical → DRE preserva dados essenciais
 * 
 * @param original - DREData original
 * @returns Objeto com status de validação e erros encontrados
 * 
 * @example
 * ```typescript
 * const dreData = await loadDRE();
 * const historical = dreToHistoricalData(dreData);
 * const backToDRE = historicalDataToDRE(historical);
 * const validation = validateConversion(dreData, backToDRE);
 * 
 * if (!validation.isValid) {
 *   console.error('Conversão com perdas:', validation.errors);
 * }
 * ```
 */
export function validateConversion(
  original: DREData | Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'>,
  converted: Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): { isValid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validar Sétima
  const originalSetima = 'setima' in original && original.setima.years 
    ? original.setima.years 
    : [];
  const convertedSetima = converted.setima.years;

  if (originalSetima.length !== convertedSetima.length) {
    errors.push(
      `Sétima: número de anos diferente (original: ${originalSetima.length}, convertido: ${convertedSetima.length})`
    );
  }

  // Validar campos essenciais de cada ano
  originalSetima.forEach((origYear, index) => {
    const convYear = convertedSetima[index];
    if (!convYear) return;

    // Validar receita (tolerância de 0.01%)
    const receitaDiff = Math.abs(origYear.receitaLiquida - convYear.receitaLiquida);
    const receitaTolerance = origYear.receitaLiquida * 0.0001;
    if (receitaDiff > receitaTolerance) {
      errors.push(
        `Sétima ${origYear.year}: receita divergente (original: ${origYear.receitaLiquida}, convertido: ${convYear.receitaLiquida})`
      );
    }

    // Validar EBITDA
    const ebitdaDiff = Math.abs(origYear.ebitda - convYear.ebitda);
    const ebitdaTolerance = Math.abs(origYear.ebitda) * 0.0001;
    if (ebitdaDiff > ebitdaTolerance) {
      errors.push(
        `Sétima ${origYear.year}: EBITDA divergente (original: ${origYear.ebitda}, convertido: ${convYear.ebitda})`
      );
    }

    // Warning: outros campos zerados
    if (origYear.custosDirectos > 0 || origYear.despesasOperacionais > 0) {
      warnings.push(
        `Sétima ${origYear.year}: campos detalhados (custos, despesas) foram perdidos na conversão`
      );
    }
  });

  // Validar 555 Studios (mesma lógica)
  const originalStudio555 = 'studio555' in original && original.studio555.years 
    ? original.studio555.years 
    : [];
  const convertedStudio555 = converted.studio555.years;

  if (originalStudio555.length !== convertedStudio555.length) {
    errors.push(
      `555 Studios: número de anos diferente (original: ${originalStudio555.length}, convertido: ${convertedStudio555.length})`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Verifica se DREData contém dados válidos para valuation
 * 
 * @param dreData - Dados DRE a validar
 * @returns true se contém ao menos um ano com receita > 0
 */
export function hasValidDataForValuation(
  dreData: DREData | Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'> | null
): boolean {
  if (!dreData) return false;

  const setimaYears = 'setima' in dreData && dreData.setima.years 
    ? dreData.setima.years 
    : [];
  const studio555Years = 'studio555' in dreData && dreData.studio555.years 
    ? dreData.studio555.years 
    : [];

  const hasSetimaData = setimaYears.some(y => y.receitaLiquida > 0);
  const hasStudio555Data = studio555Years.some(y => y.receitaLiquida > 0);

  return hasSetimaData || hasStudio555Data;
}

/**
 * Extrai o ano mais recente dos dados históricos
 * Útil para determinar o ano base para projeções
 * 
 * @param historicalData - Array de dados históricos
 * @returns Ano mais recente ou null se vazio
 */
export function getLatestYear(historicalData: HistoricalData[]): number | null {
  if (historicalData.length === 0) return null;
  
  const years = historicalData.map(d => d.year);
  return Math.max(...years);
}

/**
 * Calcula receita total combinada (Sétima + 555) de um ano específico
 * 
 * @param historicalData - Array de dados históricos
 * @param year - Ano a consultar
 * @returns Receita total ou 0 se ano não encontrado
 */
export function getTotalRevenue(historicalData: HistoricalData[], year: number): number {
  const yearData = historicalData.find(d => d.year === year);
  if (!yearData) return 0;

  return yearData.setima.receita + yearData.studio555.receita;
}

/**
 * Calcula EBITDA total combinado (Sétima + 555) de um ano específico
 * 
 * @param historicalData - Array de dados históricos
 * @param year - Ano a consultar
 * @returns EBITDA total ou 0 se ano não encontrado
 */
export function getTotalEBITDA(historicalData: HistoricalData[], year: number): number {
  const yearData = historicalData.find(d => d.year === year);
  if (!yearData) return 0;

  return yearData.setima.ebitda + yearData.studio555.ebitda;
}
